import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
    ArrowLeft,
    Calendar,
    User01,
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { orderService } from '@/data/mock-orders';
import { accountService } from '@/data/mock-accounts';
import type { Order } from '@/types/order';
import { useTranslation } from 'react-i18next';

export const ReceiverOrderDetailPage = () => {
    const { t } = useTranslation(['pages:orders', 'common', 'types']);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [order, setOrder] = useState<Order | null>(null);
    const [shipper, setShipper] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    const getStatusColor = (status: Order['status']) => {
        switch (status) {
            case 'Pending': return 'warning';
            case 'Accepted': return 'success';
            case 'Rejected': return 'error';
            case 'Modified': return 'warning';
            case 'Confirmed': return 'success';
            case 'Cancelled': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: Order['status']) => {
        return t(`status.${status.toLowerCase()}`, { ns: 'types' });
    };

    const loadOrderData = async () => {
        if (!id) return;

        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        // Load order
        const orderData = orderService.getById(id);
        if (!orderData) {
            setIsLoading(false);
            return;
        }
        setOrder(orderData);

        // Load shipper info
        const shipperData = accountService.getById(orderData.shipperId);
        setShipper(shipperData);

        setIsLoading(false);
    };

    useEffect(() => {
        loadOrderData();
    }, [id]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading')}</p>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <p className="text-lg text-tertiary mb-4">{t('detail.notFound')}</p>
                <Button onClick={() => navigate('/receiver/orders')} color="secondary">
                    {t('detail.backToList')}
                </Button>
            </div>
        );
    }


    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={() => navigate('/receiver/orders')}
                        color="secondary"
                        size="sm"
                        iconLeading={ArrowLeft}
                    >
                        {t('detail.back')}
                    </Button>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">
                            {t('detail.title')} #{order.id}
                        </h1>
                        <p className="text-tertiary mt-1">
                            {t('detail.subtitle')}
                        </p>
                    </div>
                </div>
                <Badge color={getStatusColor(order.status)} size="lg">
                    {translateStatus(order.status)}
                </Badge>
            </div>

            {/* Order Info Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg p-3 bg-brand-50 text-brand-600">
                            <User01 className="size-6" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-tertiary">{t('detail.shipper')}</p>
                            <p className="text-lg font-semibold text-primary">
                                {shipper?.legalName || order.shipperId}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg p-3 bg-success-50 text-success-600">
                            <Calendar className="size-6" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-tertiary">{t('detail.targetWeek')}</p>
                            <p className="text-lg font-semibold text-primary">
                                Settimana {order.targetWeek}/{order.targetYear}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg p-3 bg-warning-50 text-warning-600">
                            <Clock className="size-6" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-tertiary">{t('detail.createdAt')}</p>
                            <p className="text-lg font-semibold text-primary">
                                {order.createdAt.toLocaleDateString('it-IT')}
                            </p>
                        </div>
                    </div>
                </div>

                {order.respondedAt && (
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <div className="flex items-center gap-3">
                            <div className="rounded-lg p-3 bg-brand-50 text-brand-600">
                                <CheckCircle className="size-6" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-tertiary">{t('detail.respondedAt')}</p>
                                <p className="text-lg font-semibold text-primary">
                                    {order.respondedAt.toLocaleDateString('it-IT')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Proposed Changes - Mostra quando lo Shipper ha richiesto modifiche */}
            {order.status === 'Modified' && order.proposedChanges && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary border-l-4 border-warning-600">
                    <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                        <AlertCircle className="size-5 text-warning-600" />
                        {t('detail.proposedChanges')}
                    </h2>
                    <div className="rounded-lg bg-warning-50 p-4 mb-4">
                        <p className="text-sm text-warning-700 whitespace-pre-wrap">{order.proposedChanges}</p>
                    </div>
                    {!order.changesAccepted && (
                        <div className="flex gap-3">
                            <Button
                                onClick={async () => {
                                    if (!order) return;
                                    
                                    // Accetta le modifiche e reimposta lo status a Pending per indicare che l'ordine è stato aggiornato
                                    orderService.update(order.id, { 
                                        changesAccepted: true, 
                                        status: 'Pending'
                                    });
                                    
                                    // Crea attività per lo Shipper per notificare che l'ordine è stato aggiornato
                                    const shipperAccount = accountService.getById(order.shipperId);
                                    if (shipperAccount) {
                                        const { activityService } = await import('@/data/mock-activities');
                                        activityService.add({
                                            id: `order-${order.id}-updated`,
                                            audience: 'shipper',
                                            type: 'status_update',
                                            title: `Ordine #${order.id} aggiornato`,
                                            description: `Il Receiver ha accettato le modifiche e ha reinviato l'ordine aggiornato.`,
                                            timestamp: new Date(),
                                            isRead: false,
                                            actions: [
                                                { label: 'Vedi dettaglio ordine', href: `/shipper/orders/${order.id}` }
                                            ],
                                            metadata: {
                                                orderId: order.id,
                                                receiverId: order.receiverId,
                                                shipperId: order.shipperId
                                            }
                                        });
                                    }
                                    
                                    // Ricarica l'ordine aggiornato
                                    await loadOrderData();
                                }}
                                color="primary"
                                size="md"
                                iconLeading={CheckCircle}
                            >
                                {t('detail.acceptChangesAndResend') || 'Accetta Modifiche e Re-invia Ordine'}
                            </Button>
                            <Button
                                onClick={() => {
                                    if (!order) return;
                                    // Rifiuta le modifiche
                                    orderService.update(order.id, { status: 'Cancelled' });
                                    navigate('/receiver/orders');
                                }}
                                color="secondary"
                                size="md"
                                iconLeading={XCircle}
                            >
                                {t('detail.rejectChanges')}
                            </Button>
                        </div>
                    )}
                    {order.changesAccepted && (
                        <div className="rounded-lg bg-success-50 p-4">
                            <p className="text-sm text-success-700">
                                {t('detail.changesAcceptedMessage') || 'Le modifiche sono state accettate e l\'ordine è stato reinviato allo Shipper.'}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Packing List Section */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.packingList.title')}</h2>
                <div className="space-y-4">
                    <div>
                        <p className="text-sm font-medium text-tertiary mb-2">
                            {t('detail.packingList.requested')}
                        </p>
                        <div className="rounded-lg bg-secondary p-4">
                            <p className="text-sm text-primary whitespace-pre-wrap">
                                {order.packingList.requested || t('detail.packingList.notSpecified')}
                            </p>
                        </div>
                    </div>
                    {order.packingList.provided && (
                        <div>
                            <p className="text-sm font-medium text-tertiary mb-2">
                                {t('detail.packingList.provided')}
                            </p>
                            <div className="rounded-lg bg-secondary p-4">
                                <p className="text-sm text-primary whitespace-pre-wrap">
                                    {order.packingList.provided}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Notes Section */}
            {order.notes && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.notes')}</h2>
                    <div className="rounded-lg bg-secondary p-4">
                        <p className="text-sm text-primary whitespace-pre-wrap">{order.notes}</p>
                    </div>
                </div>
            )}

            {/* Rejection Reason */}
            {order.status === 'Rejected' && order.rejectionReason && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary border-l-4 border-error-600">
                    <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                        <XCircle className="size-5 text-error-600" />
                        {t('detail.rejectionReason')}
                    </h2>
                    <div className="rounded-lg bg-error-50 p-4">
                        <p className="text-sm text-error-700 whitespace-pre-wrap">{order.rejectionReason}</p>
                    </div>
                </div>
            )}

            {/* Contractual Info */}
            {order.contractualInfo && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.contractualInfo.title')}</h2>
                    <div className="space-y-3">
                        {order.contractualInfo.contractNumber && (
                            <div>
                                <p className="text-sm font-medium text-tertiary">{t('detail.contractualInfo.contractNumber')}</p>
                                <p className="text-sm text-primary">{order.contractualInfo.contractNumber}</p>
                            </div>
                        )}
                        {order.contractualInfo.contractDate && (
                            <div>
                                <p className="text-sm font-medium text-tertiary">{t('detail.contractualInfo.contractDate')}</p>
                                <p className="text-sm text-primary">
                                    {order.contractualInfo.contractDate.toLocaleDateString('it-IT')}
                                </p>
                            </div>
                        )}
                        {order.contractualInfo.terms && (
                            <div>
                                <p className="text-sm font-medium text-tertiary">{t('detail.contractualInfo.terms')}</p>
                                <p className="text-sm text-primary">{order.contractualInfo.terms}</p>
                            </div>
                        )}
                        {order.contractualInfo.notes && (
                            <div>
                                <p className="text-sm font-medium text-tertiary">{t('detail.contractualInfo.notes')}</p>
                                <p className="text-sm text-primary">{order.contractualInfo.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Order Items */}
            {order.orderItems && order.orderItems.length > 0 && (
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.orderItems.title')}</h2>
                    <div className="space-y-2">
                        {order.orderItems.map((item) => (
                            <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                                <div>
                                    <p className="text-sm font-medium text-primary">{item.description}</p>
                                    {item.notes && (
                                        <p className="text-xs text-tertiary mt-1">{item.notes}</p>
                                    )}
                                </div>
                                <div className="text-sm text-tertiary">
                                    {item.quantity} {item.unit}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

